<template>
  <div class="columns">
    <div class="column">
      <div class="timeline" v-if="hasNotes">
        <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
        <br/>
        <div class="timeline-header">
          <span class="tag is-small is-primary">Newer</span>
        </div>
        <div class="timeline-item" v-for="note in limitBy(this.notes, this.noteRange)" :key="note.id">
          <div class="timeline-marker is-image is-32x32">
            <img src="https://bulma.io/assets/images/placeholders/32x32.png">
          </div>
          <div class="timeline-content" style="width: 100%;">
            <p class="heading is-inline-block" :title="note.createdAt | formatDate">{{note.createdAt | formatHumanDate}} - {{note.createdBy}}</p>
            <i class="icon fas fa-trash-alt fa-xs pointer" @click="onRequestDeleteNote(note)" v-if="!note.isDeleted" title="Delete"/>
            <i class="icon fal fa-trash-restore-alt fa-xs pointer" @click="onRequestRestoreNote(note)" v-if="note.isDeleted" title="Restore"/>
            <i class="icon fab fa-hotjar pointer fa-xs" :class="{'has-text-danger': note.isHotNote}" @click="onToggleHotNote(note)"/>
            <i class="icon fas fa-dollar-sign pointer fa-xs" v-if="entity === 'deal' && note.type === 'Finance'" title="Finance"/>
            <i class="icon fas fa-handshake pointer fa-xs" v-if="entity === 'deal' && note.type === 'Sale'" title="Sale"/>
            <i class="icon far fa-thumbtack pointer fa-xs" :class="{'has-text-success': note.pinned}" :title="getPinTitle(note)" @click="onTogglePinned(note)"/>
            <p class="is-size-7 note-item" :class="{'note-item-deleted':note.isDeleted, 'has-text-danger':note.isHotNote}">
              <span v-if="isHtml" v-html="note.note"></span>
              <span v-else>{{note.note}}</span>
            </p>
          </div>
        </div>
        <header class="timeline-header">
          <span class="tag is-small is-primary" @click="showMoreNotes" v-if="hasMoreNotes">Older...</span>
          <span class="tag is-small is-primary" @click="showMoreNotes" v-else>Older</span>
        </header>
      </div>
      <div v-else class="column is-12 is-size-7 has-text-info">
        <p>
          There are no notes available
        </p>
      </div>
    </div>
    <confirm-delete-modal :usePortal="true" v-if="isDeleteNoteConfirmationActive" title="Confirm Delete Note" @on-confirm-delete="onNoteDelete" @on-cancel-delete="isDeleteNoteConfirmationActive = false">
      Are you sure you wish to delete the selected {{entity}} note?
    </confirm-delete-modal>
    <b-loading :is-full-page="false" :active.sync="isSaving" :canCancel="false"></b-loading>
  </div>
</template>

<script>

import inventoryService from '@/services/inventoryService'
import dealService from '@/services/dealService'
import serviceRequestService from '@/services/serviceRequestService'
import scheduledPaymentService from '@/services/dealScheduledPaymentService'
import roServiceService from '@/services/roServiceService'
import paymentTrackingService from '@/services/paymentTrackingService'
import customerService from '@/services/customerService'
import wholesaleReceivableService from '@/services/wholesaleReceivableService'
import partsService from '@/services/partsService'
import vehiclePartService from '@/services/vehiclePartService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import ConfirmDeleteModalComponent from '@/components/generic/ConfirmDeleteModalComponent'
import serviceRequestLineService from '@/services/serviceRequestLineService'
import eventBus from '@/eventBus'
import Vue2Filters from 'vue2-filters'
import utilitiesMixin from '@/mixins/generic/utilities'

export default {
  mixins: [utilitiesMixin, Vue2Filters.mixin],
  name: 'NoteTimelineView',
  components: {
    'error-display-component': ErrorDisplayComponent,
    'confirm-delete-modal': ConfirmDeleteModalComponent
  },
  props: {
    value: {
      type: Array,
      default: null
    },
    entity: {
      type: String,
      default: ''
    },
    isHtml: {
      type: Boolean,
      default: false
    },
    showDeleteModal: {
      type: Boolean,
      default: true
    },
    // When set, inventory note changes emit a local notes-changed instead of the global inventory-changed, so a host
    // living inside a grid row (CaptureNewInventoryNote) is not destroyed by the grid reload; the host refreshes later.
    deferInventoryChanged: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      notes: this.value,
      defaultNoteRange: 5,
      noteRange: 5,
      isDeleteNoteConfirmationActive: false,
      selectedNote: null,
      isSaving: false,
      serverErrors: [],
      serverErrorMessage: '',
      isDestroyed: false
    }
  },
  computed: {
    hasNotes: function () {
      return this.notes && this.notes.length > 0
    },
    hasMoreNotes: function () {
      return this.notes.length > this.noteRange
    }
  },
  methods: {
    onToggleHotNote: function (note) {
      this.selectedNote = note
      this.serverErrorMessage = ''
      this.serverErrors = []
      if (this.entity === 'deal') {
        this.dealServiceToggleNoteHotStatus()
      } else if (this.entity === 'inventory') {
        this.inventoryServiceToggleNoteHotStatus()
      } else if (this.entity === 'service-request') {
        this.serviceRequestServiceToggleNoteHotStatus()
      } else if (this.entity === 'scheduled-payment') {
        this.scheduledPaymentServiceToggleNoteHotStatus()
      } else if (this.entity === 'ro-service') {
        this.roServiceServiceToggleNoteHotStatus()
      } else if (this.entity === 'payment-tracking') {
        this.paymentTrackingServiceToggleNoteHotStatus()
      } else if (this.entity === 'customer') {
        this.customerServiceToggleNoteHotStatus()
      } else if (this.entity === 'wholesale-receivable') {
        this.wholesaleReceivableServiceToggleNoteHotStatus()
      } else if (this.entity === 'parts') {
        this.partsServiceToggleNoteHotStatus()
      } else if (this.entity === 'service-request-line') {
        this.serviceRequestLineServiceToggleNoteHotStatus()
      } else if (this.entity === 'vehicle-part') {
        this.vehiclePartServiceToggleNoteHotStatus()
      }
    },
    showMoreNotes: function () {
      this.noteRange += this.defaultNoteRange
    },
    onRequestDeleteNote: function (note) {
      this.selectedNote = note
      if (!this.showDeleteModal) {
        this.onNoteDelete()
      } else {
        this.isDeleteNoteConfirmationActive = true
      }
    },
    onRequestRestoreNote: function (note) {
      this.serverErrorMessage = ''
      this.serverErrors = []
      this.selectedNote = note
      if (this.entity === 'deal') {
        this.dealServiceRestore()
      } else if (this.entity === 'inventory') {
        this.inventoryServiceRestore()
      } else if (this.entity === 'service-request') {
        this.serviceRequestServiceRestore()
      } else if (this.entity === 'scheduled-payment') {
        this.scheduledPaymentServiceRestore()
      } else if (this.entity === 'ro-service') {
        this.roServiceServiceRestore()
      } else if (this.entity === 'payment-tracking') {
        this.paymentTrackingServiceRestore()
      } else if (this.entity === 'customer') {
        this.customerServiceRestore()
      } else if (this.entity === 'wholesale-receivable') {
        this.wholesaleReceivableServiceRestore()
      } else if (this.entity === 'parts') {
        this.partsServiceRestore()
      } else if (this.entity === 'service-request-line') {
        this.serviceRequestLineServiceRestore()
      } else if (this.entity === 'vehicle-part') {
        this.vehiclePartServiceToggleRestore()
      }
    },
    onNoteDelete: function () {
      this.serverErrorMessage = ''
      this.serverErrors = []
      if (this.entity === 'deal') {
        this.dealServiceDelete()
      } else if (this.entity === 'inventory') {
        this.inventoryServiceDelete()
      } else if (this.entity === 'service-request') {
        this.serviceRequestServiceDelete()
      } else if (this.entity === 'scheduled-payment') {
        this.scheduledPaymentServiceDelete()
      } else if (this.entity === 'ro-service') {
        this.roServiceServiceDelete()
      } else if (this.entity === 'payment-tracking') {
        this.paymentTrackingServiceDelete()
      } else if (this.entity === 'customer') {
        this.customerServiceDelete()
      } else if (this.entity === 'wholesale-receivable') {
        this.wholesaleReceivableServiceDelete()
      } else if (this.entity === 'parts') {
        this.partsServiceDelete()
      } else if (this.entity === 'service-request-line') {
        this.serviceRequestLineServiceDelete()
      } else if (this.entity === 'vehicle-part') {
        this.vehiclePartServiceDelete()
      }
    },
    dealServiceDelete: function () {
      let that = this
      this.isSaving = true
      dealService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-deal-list')
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    inventoryServiceDelete: function () {
      let that = this
      this.isSaving = true
      inventoryService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          that.notifyInventoryChanged(that.selectedNote.inventoryId)
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    serviceRequestServiceDelete: function () {
      let that = this
      that.isSaving = true
      serviceRequestService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('service-request-changed', {id: that.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    scheduledPaymentServiceDelete: function () {
      let that = this
      that.isSaving = true
      scheduledPaymentService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('payment-schedule-item-changed', {id: that.selectedNote.scheduledPaymentId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    roServiceServiceDelete: function () {
      let that = this
      this.isSaving = true
      roServiceService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('ro-service-item-changed', {id: that.selectedNote.roServiceId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    paymentTrackingServiceDelete: function () {
      let that = this
      this.isSaving = true
      paymentTrackingService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('payment-tracking-item-changed', {id: that.selectedNote.paymentTrackingId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    customerServiceDelete: function () {
      let that = this
      this.isSaving = true
      customerService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-customer-list', {id: that.selectedNote.customerId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    wholesaleReceivableServiceDelete: function () {
      let that = this
      this.isSaving = true
      wholesaleReceivableService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-wholesale-receivable-list', {id: that.selectedNote.wholesaleReceivableId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    partsServiceDelete: function () {
      let that = this
      this.isSaving = true
      partsService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-parts-list', {id: that.selectedNote.partsId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    serviceRequestLineServiceDelete: function () {
      let that = this
      this.isSaving = true
      serviceRequestLineService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('service-request-line-changed', {id: that.selectedNote.serviceRequestId})
          that.showDeleteToastMessageIfRequired()
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    vehiclePartServiceDelete: function () {
      let that = this
      this.isSaving = true
      vehiclePartService.deleteNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showDeleteToastMessageIfRequired()
          that.selectedNote.isDeleted = true
          that.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-vehicle-part-list', {id: that.selectedNote.vehiclePartId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    dealServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      dealService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-deal-list')
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    inventoryServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      inventoryService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          this.notifyInventoryChanged(this.selectedNote.inventoryId)
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    serviceRequestServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      serviceRequestService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('service-request-changed', {id: this.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    scheduledPaymentServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      scheduledPaymentService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('payment-schedule-item-changed', {id: this.selectedNote.scheduledPaymentId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    roServiceServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      roServiceService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('ro-service-item-changed', {id: this.selectedNote.roServiceId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    paymentTrackingServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      paymentTrackingService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('payment-tracking-item-changed', {id: this.selectedNote.paymentTrackingId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    customerServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      customerService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-customer-list', {id: this.selectedNote.customerId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    wholesaleReceivableServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      wholesaleReceivableService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-wholesale-receivable-list', {id: this.selectedNote.wholesaleReceivableId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    partsServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      partsService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-parts-list', {id: this.selectedNote.partsId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    serviceRequestLineServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      serviceRequestLineService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('service-request-line-changed', {id: this.selectedNote.serviceRequestLineId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    vehiclePartServiceToggleNoteHotStatus: function () {
      this.isSaving = true
      vehiclePartService.toggleNoteHotStatus(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.isHotNote = !this.selectedNote.isHotNote
          this.isDeleteNoteConfirmationActive = false
          eventBus.$emit('reload-vehicle-part-list', {id: this.selectedNote.vehiclePartId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    getPinTitle: function (note) {
      return note.pinned ? 'Unpin' : 'Pin to the top'
    },
    onTogglePinned: function (note) {
      this.selectedNote = note
      this.serverErrorMessage = ''
      this.serverErrors = []
      if (this.entity === 'deal') {
        this.dealServiceToggleNotePinned()
      } else if (this.entity === 'inventory') {
        this.inventoryServiceToggleNotePinned()
      } else if (this.entity === 'service-request') {
        this.serviceRequestServiceToggleNotePinned()
      } else if (this.entity === 'scheduled-payment') {
        this.scheduledPaymentServiceToggleNotePinned()
      } else if (this.entity === 'ro-service') {
        this.roServiceServiceToggleNotePinned()
      } else if (this.entity === 'payment-tracking') {
        this.paymentTrackingServiceToggleNotePinned()
      } else if (this.entity === 'customer') {
        this.customerServiceToggleNotePinned()
      } else if (this.entity === 'wholesale-receivable') {
        this.wholesaleReceivableServiceToggleNotePinned()
      } else if (this.entity === 'parts') {
        this.partsServiceToggleNotePinned()
      } else if (this.entity === 'service-request-line') {
        this.serviceRequestLineServiceToggleNotePinned()
      } else if (this.entity === 'vehicle-part') {
        this.vehiclePartServiceToggleNotePinned()
      }
    },
    dealServiceToggleNotePinned: function () {
      this.isSaving = true
      dealService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('reload-deal-list')
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    inventoryServiceToggleNotePinned: function () {
      this.isSaving = true
      inventoryService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          this.notifyInventoryChanged(this.selectedNote.inventoryId)
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    serviceRequestServiceToggleNotePinned: function () {
      this.isSaving = true
      serviceRequestService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('service-request-changed', {id: this.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    scheduledPaymentServiceToggleNotePinned: function () {
      this.isSaving = true
      scheduledPaymentService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('payment-schedule-item-changed', {id: this.selectedNote.scheduledPaymentId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    roServiceServiceToggleNotePinned: function () {
      this.isSaving = true
      roServiceService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('ro-service-item-changed', {id: this.selectedNote.roServiceId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    paymentTrackingServiceToggleNotePinned: function () {
      this.isSaving = true
      paymentTrackingService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('payment-tracking-item-changed', {id: this.selectedNote.paymentTrackingId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    customerServiceToggleNotePinned: function () {
      this.isSaving = true
      customerService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('reload-customer-list', {id: this.selectedNote.customerId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    wholesaleReceivableServiceToggleNotePinned: function () {
      this.isSaving = true
      wholesaleReceivableService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('reload-wholesale-receivable-list', {id: this.selectedNote.wholesaleReceivableId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    partsServiceToggleNotePinned: function () {
      this.isSaving = true
      partsService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('reload-parts-list', {id: this.selectedNote.partsId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    serviceRequestLineServiceToggleNotePinned: function () {
      this.isSaving = true
      serviceRequestLineService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('service-request-line-changed', {id: this.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    vehiclePartServiceToggleNotePinned: function () {
      this.isSaving = true
      vehiclePartService.toggleNotePinned(this.selectedNote).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.selectedNote.pinned = !this.selectedNote.pinned
          this.notes = response.notes
          eventBus.$emit('reload-vehicle-part-list', {id: this.selectedNote.vehiclePartId})
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    dealServiceRestore: function () {
      let that = this
      this.isSaving = true
      dealService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('reload-deal-list')
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    inventoryServiceRestore: function () {
      this.isSaving = true
      let that = this
      inventoryService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          that.notifyInventoryChanged(that.selectedNote.inventoryId)
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    serviceRequestServiceRestore: function () {
      this.isSaving = true
      let that = this
      serviceRequestService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('service-request-changed', {id: that.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    scheduledPaymentServiceRestore: function () {
      this.isSaving = true
      let that = this
      scheduledPaymentService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('payment-schedule-item-changed', {id: that.selectedNote.scheduledPaymentId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    roServiceServiceRestore: function () {
      let that = this
      this.isSaving = true
      roServiceService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('ro-service-item-changed', {id: that.selectedNote.roServiceId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    paymentTrackingServiceRestore: function () {
      let that = this
      this.isSaving = true
      paymentTrackingService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('payment-tracking-item-changed', {id: that.selectedNote.paymentTrackingId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    customerServiceRestore: function () {
      let that = this
      this.isSaving = true
      customerService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('reload-customer-list', {id: that.selectedNote.customerId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    wholesaleReceivableServiceRestore: function () {
      let that = this
      this.isSaving = true
      wholesaleReceivableService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('reload-wholesale-receivable-list', {id: that.selectedNote.wholesaleReceivableId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    partsServiceRestore: function () {
      let that = this
      this.isSaving = true
      partsService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('reload-parts-list', {id: that.selectedNote.partsId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    serviceRequestLineServiceRestore: function () {
      let that = this
      this.isSaving = true
      serviceRequestLineService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('service-request-line-changed', {id: that.selectedNote.serviceRequestId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    vehiclePartServiceToggleRestore: function () {
      let that = this
      this.isSaving = true
      vehiclePartService.restoreNote(that.selectedNote).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.showRestoreToastMessage()
          that.selectedNote.isDeleted = false
          eventBus.$emit('reload-vehicle-part-list', {id: that.selectedNote.partsId})
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    notifyInventoryChanged: function (inventoryId) {
      // Once destroyed (host window closed mid-request) nobody hears the local event, so refresh globally
      if (this.deferInventoryChanged && !this.isDestroyed) {
        this.$emit('notes-changed', {id: inventoryId})
      } else {
        eventBus.$emit('inventory-changed', {id: inventoryId})
      }
    },
    showRestoreToastMessage: function () {
      this.successToast('Success! Note has been restored.')
    },
    showDeleteToastMessageIfRequired: function () {
      if (!this.showDeleteModal) {
        this.successToast('Success! Note has been deleted.')
      }
    }
  },
  watch: {
    value: function (newValue, oldValue) {
      this.notes = newValue
    }
  },
  destroyed () {
    this.isDestroyed = true
  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

  .box {
    background-color: #f6f8fa;
  }

  .pointer {
    cursor: pointer;
  }

</style>
